import { Log } from 'ng2-logger';
import { put, takeEvery, all } from 'redux-saga/effects';
import AppFactory from '../../services/app-factory';
import MockAPIClient from '../../services/mock-api-client';
import PerculusApiClient from '../../services/perculus-api-client';
import Translator from '../../services/translate-factory';
import { routes as Routes } from '../../store/constants/routes';
import * as GT from '../../tools/general-tools';
import * as Actions from '../actions/general';
import * as actions from '../constants/all';
import { TwoFactorValidationResult } from '../constants/login-const';
import type * as Types from '../types';

declare const appSettings: Types.IAppSettings;

const T = Translator.create();
const L = Log.create('login-saga');
const AF = AppFactory.create();

function* setAuthToken(action: Types.IApiRequestAction, isOIDC = false): Generator<any, any, any> {
  if (appSettings.CONSTRUCTOR_IDP) {
    yield put(Actions.Notification('notification_user_validation_correct', 'gen_login'));
    const mockResponse = new Response(JSON.stringify({ access_token: 'cookie-based-auth' }), {
      status: 200,
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });
    yield put(Actions.ApiResponse(action, mockResponse, { access_token: 'cookie-based-auth' }));
    AF.setAppModuleReady(actions.gen.CORE_AUTH_USER, false);
    yield put(Actions.ApiRequest(actions.gen.CORE_AUTH_USER));
    return;
  }

  // Regular token-based authentication flow
  const MockAPI: MockAPIClient = new MockAPIClient();
  let response: Response;
  if (isOIDC) {
    response = yield MockAPI.GetResponse(MockAPI.GetAuthTokenOIDC());
  } else {
    response = yield MockAPI.GetResponse(MockAPI.GetAuthToken(action.body));
  }
  const content: any = yield MockAPI.GetContent(response);
  if (response && response.status === 200) {
    if (content?.access_token) {
      yield put(Actions.Notification('notification_user_validation_correct', 'gen_login'));
    } else {
      // Clear the failed auth state
      MockAPI._accessToken = '';
      yield put(Actions.ApiRequest(actions.gen.CORE_AUTH_USER, { clear: true }));
      yield put(
        Actions.ShowModal({
          title: T.t('gen_login_failed'),
          body: T.t('gen_login_auth_error'),
          name: 'login_failed',
          icon: 'error_outline',
          iconColor: 'red',
        }),
      );
    }
  } else if (response && response.status === 412) {
    // Handle 2FA required - don't clear auth state, let the callback handle it
    // The login form callback will handle showing the 2FA modal
  } else {
    if (content?.code) {
      // Clear the failed auth state
      MockAPI._accessToken = '';
      yield put(Actions.ApiRequest(actions.gen.CORE_AUTH_USER, { clear: true }));
      yield put(
        Actions.ShowModal({
          title: T.t('gen_login_failed'),
          body: content.details,
          name: 'login_failed',
          icon: 'error_outline',
          iconColor: 'red',
        }),
      );
    } else {
      yield put(Actions.Notification('gen_error_occurred', 'gen_warning', 'danger'));
      // Clear the failed auth state
      MockAPI._accessToken = '';
      yield put(Actions.ApiRequest(actions.gen.CORE_AUTH_USER, { clear: true }));
    }
  }

  yield put(Actions.ApiResponse(action, response, content));

  if (content?.access_token) {
    MockAPI._accessToken = content.access_token;
    AF.setAppModuleReady(actions.gen.CORE_AUTH_USER, false);
    yield put(Actions.ApiRequest(actions.gen.CORE_AUTH_USER));
  }
}

function* sendForgotPassword(action: Types.IApiRequestAction): Generator<any, any, any> {
  const papi: PerculusApiClient = new PerculusApiClient();
  const response: Response = yield papi.GetResponse(papi.SendForgotPassword(action.body));
  const content: any = yield papi.GetContent(response);
  if (response && response.status === 200) {
    yield put(Actions.Navigation(GT.Route(Routes.LOGIN)));
    if (action.body.token) {
      yield put(
        Actions.ShowModal({
          title: T.t('gen_password_reset_success'),
          body: T.t('gen_password_reset_success_body'),
          name: 'login_pw_done',
          icon: 'done_outline',
          iconColor: '#39b27b',
        }),
      );
    } else {
      yield put(
        Actions.ShowModal({
          title: T.t('gen_password_reset_request'),
          body: T.t('gen_password_reset_request_body'),
          name: 'login_forgot_pw',
          icon: 'done_outline',
          iconColor: '#39b27b',
        }),
      );
    }
  } else {
    yield put(Actions.Notification('gen_error_occurred', 'gen_warning', 'danger'));
  }

  yield put(Actions.ApiResponse(action, response, content));
}

function* resetPassword(action: Types.IApiRequestAction): Generator<any, any, any> {
  const papi: PerculusApiClient = new PerculusApiClient();
  const response: Response = yield papi.GetResponse(papi.ResetPassword(action.body));
  const content: any = yield papi.GetContent(response);
  if (response && response.status === 200) {
    yield put(Actions.Navigation(GT.Route(Routes.LOGIN)));
    if (action.body.token) {
      yield put(
        Actions.ShowModal({
          title: T.t('gen_password_reset_success'),
          body: T.t('gen_password_reset_success_body'),
          name: 'login_pw_done',
          icon: 'done_outline',
          iconColor: '#39b27b',
        }),
      );
    } else {
      yield put(
        Actions.ShowModal({
          title: T.t('gen_password_reset_success'),
          body: T.t('gen_password_reset_success_body'),
          name: 'login_pw_done',
          icon: 'done_outline',
          iconColor: '#39b27b',
        }),
      );
    }
  } else {
    yield put(Actions.Notification('gen_error_occurred', 'gen_warning', 'danger'));
  }

  yield put(Actions.ApiResponse(action, response, content));
}

function* verifyTwoFactorCode(action: Types.IApiRequestAction): Generator<any, any, any> {
  const papi: PerculusApiClient = new PerculusApiClient();
  const MockAPI: MockAPIClient = new MockAPIClient();
  const response: Response = yield papi.GetResponse(papi.VerifyTwoFactorCode(action.body));
  const content: any = yield papi.GetContent(response);
  if (response && response.status === 200) {
    // this is the same as a successful login
    MockAPI._accessToken = content.access_token;
    // update the auth state in the store
    yield put(
      Actions.ApiResponse(
        {
          type: actions.login.LOGIN_AUTH_TOKEN,
          target: actions.login.LOGIN_AUTH_TOKEN,
          body: null,
        },
        response,
        content,
      ),
    );

    AF.setAppModuleReady(actions.gen.CORE_AUTH_USER, false);
    yield put(Actions.ApiRequest(actions.gen.CORE_AUTH_USER));
    yield put(Actions.Notification('notification_user_validation_correct', 'gen_login'));
    return;
  } else if (content === TwoFactorValidationResult.UnexpectedError) {
    yield put(Actions.Notification('gen_error_unexpected', 'gen_warning', 'danger'));
  } else if (content === TwoFactorValidationResult.NotAllowed) {
    yield put(Actions.Notification('2fa_error_not_allowed', 'gen_warning', 'danger'));
  } else if (content === TwoFactorValidationResult.IncorrectCode) {
    yield put(Actions.Notification('2fa_error_invalid_code', 'gen_warning', 'danger'));
  } else if (content === TwoFactorValidationResult.CodeExpired) {
    yield put(Actions.Notification('2fa_code_expired', 'gen_warning', 'danger'));
  } else {
    yield put(Actions.Notification('2fa_error_invalid_code', 'gen_warning', 'danger'));
  }
  yield put(Actions.ApiResponse(action, response, content));
}

function* resendTwoFactorCode(action: Types.IApiRequestAction): Generator<any, any, any> {
  const papi: PerculusApiClient = new PerculusApiClient();
  const response: Response = yield papi.GetResponse(papi.ResendTwoFactorCode(action.body));
  const content: any = yield papi.GetContent(response);
  yield put(Actions.ApiResponse(action, response, content));
}

function* validateIdpCallback(action: Types.IApiRequestAction): Generator<any, any, any> {
  try {
    L.data('Processing IDP callback validation in saga...');

    // Check if we're using constructor_idp
    if (!appSettings.CONSTRUCTOR_IDP) {
      L.error('Constructor IDP is not enabled in app settings');
      yield put(
        Actions.Notification('Authentication method not configured', 'gen_login_failed', 'danger'),
      );
      yield put(Actions.Navigation(GT.Route(Routes.LOGIN)));
      return;
    }

    const { redirectUrl, error, errorDescription } = action.body || {};

    // Check for authentication errors from IDP
    if (error) {
      L.error('IDP authentication error:', error, errorDescription);
      yield put(
        Actions.Notification(
          errorDescription || 'Authentication failed',
          'gen_login_failed',
          'danger',
        ),
      );
      yield put(Actions.Navigation(GT.Route(Routes.LOGIN)));
      return;
    }

    let userContent: any = null;
    let response: Response;

    try {
      L.data('Validating HTTP-only cookie authentication...');

      // Use the proper authentication API client
      const authAPI: PerculusApiClient = new PerculusApiClient();
      response = yield authAPI.GetResponse(authAPI.GetAuthUser());

      if (response && response.status === 200) {
        userContent = yield authAPI.GetContent(response);
        L.data('HTTP-only cookie authentication successful:', userContent);

        // Set the auth state to indicate cookie-based authentication
        const cookieAuthState = {
          access_token: 'cookie-based-auth',
          token_type: 'cookie',
          expires_in: null,
          scope: null,
        };

        // Dispatch the auth token action to set the authentication state
        yield put(
          Actions.ApiResponse(
            {
              type: actions.login.LOGIN_AUTH_TOKEN,
              target: actions.login.LOGIN_AUTH_TOKEN,
              body: null,
            },
            response,
            cookieAuthState,
          ),
        );

        // Now trigger the user authentication flow
        // The CORE_AUTH_USER saga will handle navigation based on user role
        yield put(Actions.ApiRequest(actions.gen.CORE_AUTH_USER));

        // Show success notification
        yield put(Actions.Notification('Login successful', 'gen_login', 'success'));

        // Handle custom redirect URL if provided
        if (redirectUrl?.startsWith('/') && redirectUrl !== '/') {
          // Use a small delay to ensure CORE_AUTH_USER completes first
          setTimeout(() => {
            AF.dispatcher(Actions.Navigation(redirectUrl));
          }, 1000);
        }
      } else if (response && response.status === 401) {
        L.error('HTTP-only cookie authentication failed - unauthorized');
        yield put(
          Actions.Notification(
            'Authentication failed - please log in again',
            'gen_login_failed',
            'danger',
          ),
        );
        yield put(Actions.Navigation(GT.Route(Routes.LOGIN)));
      } else {
        L.error(
          'Unexpected response status during authentication:',
          response ? response.status : 'No response',
        );
        yield put(
          Actions.Notification('Authentication verification failed', 'gen_login_failed', 'danger'),
        );
        yield put(Actions.Navigation(GT.Route(Routes.LOGIN)));
      }

      if (response) {
        yield put(Actions.ApiResponse(action, response, userContent));
      } else {
        // Create a mock error response if no response received
        const errorResponse = new Response(JSON.stringify({ error: 'No response received' }), {
          status: 500,
          headers: new Headers({ 'Content-Type': 'application/json' }),
        });
        yield put(Actions.ApiResponse(action, errorResponse, { error: 'No response' }));
      }
    } catch (verifyErr) {
      L.error('Session verification failed:', verifyErr);
      yield put(
        Actions.Notification(
          'Session verification failed - network error',
          'gen_login_failed',
          'danger',
        ),
      );
      yield put(Actions.Navigation(GT.Route(Routes.LOGIN)));

      // Create a mock error response for the API response
      const errorResponse = new Response(JSON.stringify({ error: 'Network error' }), {
        status: 500,
        headers: new Headers({ 'Content-Type': 'application/json' }),
      });
      yield put(Actions.ApiResponse(action, errorResponse, { error: verifyErr }));
      return;
    }
  } catch (err) {
    L.error('Error in callback handling:', err);
    yield put(
      Actions.Notification(
        'Failed to process authentication callback',
        'gen_login_failed',
        'danger',
      ),
    );
    yield put(Actions.Navigation(GT.Route(Routes.LOGIN)));

    // Create a mock error response for the API response
    const errorResponse = new Response(JSON.stringify({ error: 'Callback error' }), {
      status: 500,
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });
    yield put(Actions.ApiResponse(action, errorResponse, { error: err }));
  }
}

export function* getAuthToken() {
  yield takeEvery((action: any) => action.target === actions.login.LOGIN_AUTH_TOKEN, setAuthToken);
}

export function* getAuthTokenOIDC() {
  const setAuthTokenOIDC = (action: any) => setAuthToken(action, true);
  yield takeEvery(
    (action: any) => action.target === actions.login.LOGIN_AUTH_TOKEN_OIDC,
    setAuthTokenOIDC,
  );
}

export function* getForgotPwResponse() {
  yield takeEvery(
    (action: any) => action.target === actions.login.LOGIN_FORGOT_PASSWORD,
    sendForgotPassword,
  );
}

export function* getResetPwResponse() {
  yield takeEvery(
    (action: any) => action.target === actions.login.LOGIN_RESET_PASSWORD,
    resetPassword,
  );
}

export function* getVerifyTwoFactorCode() {
  yield takeEvery(
    (action: any) => action.target === actions.login.LOGIN_VERIFY_2FA_CODE,
    verifyTwoFactorCode,
  );
}

export function* getIdpCallbackResponse() {
  yield takeEvery(
    (action: any) => action.target === actions.login.LOGIN_IDP_CALLBACK,
    validateIdpCallback,
  );
}

export default function* loginSagas() {
  yield all([
    getAuthToken(),
    getForgotPwResponse(),
    getAuthTokenOIDC(),
    getVerifyTwoFactorCode(),
    getResetPwResponse(),
    getIdpCallbackResponse(),
  ]);
}
